import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Search, Users2, Crown } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { ROLE_LABELS } from "@/lib/roles";
import { PERSON_STATUS_META } from "./peopleMeta";
import { PersonProfile } from "./PersonProfile";

export const PeopleWorkspace = () => {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [teamId, setTeamId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (teamId) params.team_id = teamId;
    axios.get(`${API}/org/people`, { params, withCredentials: true })
      .then(({ data: resp }) => setData(resp))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, q, teamId]);

  useEffect(() => { load(); }, [load]);

  if (selectedUserId) {
    return <PersonProfile userId={selectedUserId} API={API} onBack={() => setSelectedUserId(null)} />;
  }

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="people-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="people-loading">Loading…</p>;

  return (
    <Panel title="People" subtitle="Everyone's real Team, role and current schedule status — the same data behind Office." testId="people-list-tab">
      <div className="mb-4 flex flex-wrap gap-2" data-testid="people-filters">
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" data-testid="people-search-input"
            className="rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-xs font-semibold dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
        </div>
        {data.teams.length > 0 && (
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="people-team-filter"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Teams</option>
            {data.teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {data.people.length === 0 ? (
        <EmptyState icon={Users2} title="No people to show." message="No users are visible to you yet." testId="people-empty" />
      ) : (
        <div className="space-y-2" data-testid="people-rows">
          {data.people.map((p) => {
            const status = PERSON_STATUS_META[p.current_status.status] || PERSON_STATUS_META.available;
            return (
              <button key={p.user_id} type="button" onClick={() => setSelectedUserId(p.user_id)} data-testid={`people-row-${p.user_id}`}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate text-sm font-bold text-slate-900 dark:text-white">
                    {p.name}
                    {p.teams.some((t) => t.is_lead) && <Crown size={13} className="text-amber-500" data-testid={`people-row-lead-badge-${p.user_id}`} />}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {ROLE_LABELS[p.role] || p.role} · {p.teams.length > 0 ? p.teams.map((t) => t.team_name).join(", ") : "No Team"}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold ${status.cls}`} data-testid={`people-row-status-${p.user_id}`}>{status.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </Panel>
  );
};
